import { CheckCircle as CheckCircleIcon, Error as ErrorIcon, Warning as WarningIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTimetableStore } from '../../stores/timetableStore';
import { conflictTypeLabels, mockConflicts, severityColors, type Conflict, type Resolution } from './MockData';

const Conflicts: React.FC = () => {
  const { currentTimetable } = useTimetableStore();
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [expandedConflict, setExpandedConflict] = useState<string | null>(null);

  if (!currentTimetable) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="warning">No timetable available. Please generate a timetable first.</Alert>
      </Container>
    );
  }

  // Use mock conflicts for demonstration
  const allConflicts = mockConflicts;

  // Filter conflicts
  const filteredConflicts = allConflicts.filter((conflict) => {
    if (selectedSeverity !== 'all' && conflict.severity !== selectedSeverity) return false;
    if (selectedType !== 'all' && conflict.type !== selectedType) return false;
    return true;
  });

  const getSeverityIcon = (severity: Conflict['severity']) => {
    switch (severity) {
      case 'high':
        return <ErrorIcon color="error" />;
      case 'medium':
        return <WarningIcon color="warning" />;
      case 'low':
        return <CheckCircleIcon color="info" />;
      default:
        return null;
    }
  };

  const handleApplyResolution = (conflictId: string, resolution: Resolution) => {
    // Mock implementation - would apply resolution in real app
    alert(`Applying resolution: ${resolution.description}`);
    // In real app: applyResolution(resolution, currentTimetable.lessons)
  };

  const toggleConflictExpansion = (conflictId: string) => {
    setExpandedConflict(expandedConflict === conflictId ? null : conflictId);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Conflict Detection & Resolution
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {currentTimetable.name}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total Conflicts
            </Typography>
            <Typography variant="h4">{allConflicts.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="error.main">
              High Severity
            </Typography>
            <Typography variant="h4" color="error.main">
              {allConflicts.filter((c) => c.severity === 'high').length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="warning.main">
              Medium Severity
            </Typography>
            <Typography variant="h4" color="warning.main">
              {allConflicts.filter((c) => c.severity === 'medium').length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="info.main">
              Low Severity
            </Typography>
            <Typography variant="h4" color="info.main">
              {allConflicts.filter((c) => c.severity === 'low').length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Severity Filter</InputLabel>
              <Select
                value={selectedSeverity}
                label="Severity Filter"
                onChange={(e) => setSelectedSeverity(e.target.value)}
              >
                <MenuItem value="all">All Severities</MenuItem>
                <MenuItem value="high">High Only</MenuItem>
                <MenuItem value="medium">Medium Only</MenuItem>
                <MenuItem value="low">Low Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Type Filter</InputLabel>
              <Select
                value={selectedType}
                label="Type Filter"
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="teacher_double_booking">Teacher Double-Booking</MenuItem>
                <MenuItem value="room_double_booking">Room Double-Booking</MenuItem>
                <MenuItem value="group_double_booking">Student Group Double-Booking</MenuItem>
                <MenuItem value="room_capacity">Room Capacity</MenuItem>
                <MenuItem value="teacher_unavailable">Teacher Unavailable</MenuItem>
                <MenuItem value="consecutive_limit">Consecutive Limit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Conflicts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Severity</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Resolutions</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConflicts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {selectedSeverity !== 'all' || selectedType !== 'all'
                      ? 'No conflicts match the selected filters'
                      : 'No conflicts detected'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredConflicts.map((conflict) => (
                <React.Fragment key={conflict.id}>
                  <TableRow hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getSeverityIcon(conflict.severity)}
                        <Chip
                          label={conflict.severity}
                          color={severityColors[conflict.severity] as any}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conflictTypeLabels[conflict.type]}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {conflict.day.charAt(0).toUpperCase() + conflict.day.slice(1)} P{conflict.period}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conflict.description}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={conflict.suggestedResolutions.length} size="small" color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={() => toggleConflictExpansion(conflict.id)}>
                        {expandedConflict === conflict.id ? 'Hide' : 'View'} Resolutions
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Resolution Section */}
                  {expandedConflict === conflict.id && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: 'action.hover', p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Suggested Resolutions
                        </Typography>
                        <Grid container spacing={2}>
                          {conflict.suggestedResolutions.map((resolution) => (
                            <Grid item xs={12} key={resolution.id}>
                              <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" fontWeight="bold" gutterBottom>
                                      {resolution.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                      <Chip
                                        label={`Impact: ${resolution.impact}`}
                                        size="small"
                                        color={
                                          resolution.impact === 'minimal'
                                            ? 'success'
                                            : resolution.impact === 'moderate'
                                              ? 'warning'
                                              : 'error'
                                        }
                                      />
                                      <Chip
                                        label={`Confidence: ${resolution.confidence}%`}
                                        size="small"
                                        color={resolution.confidence >= 85 ? 'success' : 'warning'}
                                      />
                                      <Chip
                                        label={resolution.type.replace('_', ' ')}
                                        size="small"
                                        variant="outlined"
                                        sx={{ textTransform: 'capitalize' }}
                                      />
                                    </Box>
                                  </Box>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleApplyResolution(conflict.id, resolution)}
                                  >
                                    Apply
                                  </Button>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight="bold">
          About Conflict Resolution:
        </Typography>
        <Typography variant="caption" component="div">
          • High severity conflicts should be resolved immediately
        </Typography>
        <Typography variant="caption" component="div">
          • Resolutions are AI-generated suggestions based on timetable constraints
        </Typography>
        <Typography variant="caption" component="div">
          • Confidence scores indicate likelihood of successful resolution
        </Typography>
        <Typography variant="caption" component="div">
          • Impact levels show how much the resolution affects other lessons
        </Typography>
      </Alert>
    </Container>
  );
};

export default Conflicts;
